import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useLocation } from "react-router-dom";
import { useDropdownStyles } from "../FDFilterDropdownStyles";
import {
  EuiFieldSearch,
  EuiFilterButton,
  EuiIcon,
  EuiPopoverTitle,
  useGeneratedHtmlId,
  EuiFlexGroup,
  EuiFlexItem,
  EuiAccordion,
  EuiNotificationBadge,
  EuiLoadingSpinner,
  EuiText,
} from "@elastic/eui";
import Emitter from "services/UtilsService/Emitter";
import LookupService from "services/LookupService";
import { FilterType } from "services/ServicesTypes/Lookup";
import { getFirstNonNullUndefined, isNullOrUndefined, mergeByName } from "utils";
import { NEGATE_FILTER } from "Consts";
import globalFilterTypes from "reduxStore/globalFilter/globalFilter.types";
import { globalFilterSelector } from "reduxStore/globalFilter/globalFilter.selectors";
import { authSelector } from "reduxStore/auth/auth.selectors";

import { getDropdownData } from "../FDFilterDropdownService";
import FDFilterDropdownFooter from "../components/FDFilterDropdownFooter";
import { CustomCheckbox } from "components/CustomInputs/CustomCheckbox";
import Popover from "components/Popover/Popover";
import Badge from "components/Badge/Badge";
import { TagGroupIcon } from "components/Icons";
import "styles/components/filterDropdown/fd_filter_dropdown.css";

export const TagAccordionItem = ({
  option,
  activeFilters,
  filterItemClick,
  classes,
  setActiveFilters,
  OnClick,
  items,
  onToggle,
  isOpen,
  filterText,
  isSingleSelection = false,
}: any) => {
  const processedTagOptions = useMemo(() => {
    return (
      option?.values?.map((i: any) => {
        const fullTagName = `${option.name}: ${i?.value}`;
        return {
          ...i,
          name: i?.value,
          fullTagName,
          checked:
            activeFilters[fullTagName] === "on"
              ? "on"
              : activeFilters[fullTagName] === "off"
                ? "off"
                : undefined,
        };
      }) ?? []
    );
  }, [option, activeFilters]);

  const filteredTagOptions = useMemo(() => {
    let options = processedTagOptions;

    if (filterText?.length > 0) {
      options = processedTagOptions.filter((tagOption: any) => {
        const tagValue = tagOption.name || "";
        const tagDisplay = tagOption.display || tagValue;

        return (
          tagValue.toLowerCase().includes(filterText.toLowerCase()) ||
          tagDisplay.toLowerCase().includes(filterText.toLowerCase())
        );
      });
    } else {
      options = processedTagOptions;
    }

    return options.sort((a: any, b: any) => {
      const aDisplay = a.display || a.name || "";
      const bDisplay = b.display || b.name || "";
      return aDisplay.localeCompare(bDisplay);
    });
  }, [processedTagOptions, filterText]);

  const checkedCount = useMemo(() => {
    return processedTagOptions.filter(
      (tagOption: any) => activeFilters[tagOption.fullTagName] === "on"
    ).length;
  }, [processedTagOptions, activeFilters]);

  const negativeCheckedCount = useMemo(() => {
    return processedTagOptions.filter(
      (tagOption: any) => activeFilters[tagOption.fullTagName] === "off"
    ).length;
  }, [processedTagOptions, activeFilters]);

  const totalCheckedCount = useMemo(() => {
    return checkedCount + negativeCheckedCount;
  }, [checkedCount, negativeCheckedCount]);

  const areAllSelected = useMemo(() => {
    return processedTagOptions.length > 0 && checkedCount === processedTagOptions.length;
  }, [processedTagOptions.length, checkedCount]);

  const areAllNegativeSelected = useMemo(() => {
    return processedTagOptions.length > 0 && negativeCheckedCount === processedTagOptions.length;
  }, [processedTagOptions.length, negativeCheckedCount]);

  const handleCheckboxClick = (e: any) => {
    e.stopPropagation();

    const newActiveFilters = { ...activeFilters };

    if (areAllSelected) {
      processedTagOptions.forEach((tagOption: any) => {
        newActiveFilters[tagOption.fullTagName] = "off";
      });
    } else if (areAllNegativeSelected) {
      processedTagOptions.forEach((tagOption: any) => {
        delete newActiveFilters[tagOption.fullTagName];
      });
    } else {
      processedTagOptions.forEach((tagOption: any) => {
        newActiveFilters[tagOption.fullTagName] = "on";
      });
    }

    setActiveFilters(newActiveFilters);

    if (OnClick) {
      OnClick({
        list: newActiveFilters,
        listItems: items?.filter((i: any) => Object.keys(newActiveFilters).includes(i.name)),
        name: option.name,
        value: areAllSelected ? undefined : "on",
      });
    }
  };

  const handleTagOptionClick = (tagOption: any) => {
    const parsedSelected = {
      name: tagOption.fullTagName,
      value: tagOption.fullTagName,
      label: tagOption.fullTagName,
      display: tagOption.fullTagName,
      tagValueId: tagOption?.tagValueId,
    };

    filterItemClick(parsedSelected);
  };

  const renderTagOption = (tagOption: any) => {
    return (
      <div
        key={tagOption.fullTagName}
        className={classes.tagOptionRow}
        onClick={() => handleTagOptionClick(tagOption)}
        style={{ cursor: "pointer" }}>
        <EuiFlexGroup
          style={{
            padding: "0 4px",
          }}
          alignItems="center"
          gutterSize="s">
          {isSingleSelection && (
            <EuiFlexItem grow={false}>
              <EuiIcon type="tag" style={{ marginRight: "5px" }} size="s" color="#9170BB" />
            </EuiFlexItem>
          )}
          {!isSingleSelection && (
            <CustomCheckbox
              onClick={() => {}}
              isChecked={activeFilters[tagOption.fullTagName] === "on"}
              isExcluded={activeFilters[tagOption.fullTagName] === "off"}
            />
          )}
          <EuiFlexItem>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                fontSize: tagOption?.name?.length > 50 ? "11px" : "14px",
              }}>
              {tagOption.display ?? tagOption.name}
            </span>
          </EuiFlexItem>

          {tagOption?.ownerType !== "Account" && (
            <EuiFlexItem grow={false}>
              <Badge text={tagOption?.ownerType} />
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
      </div>
    );
  };

  if (!processedTagOptions.length) {
    return (
      <div className={classes.optionRow}>
        <span style={{ display: "flex" }}>
          {isSingleSelection && (
            <span style={{ paddingLeft: "12px", paddingRight: "8px" }}>
              <TagGroupIcon size="18" />
            </span>
          )}
          {option.display ?? option.name} (0)
        </span>
      </div>
    );
  }

  return (
    <div className={classes.tagAccordionButton}>
      <EuiAccordion
        id={`tag-accordion-${option.name}`}
        buttonContent={
          <EuiFlexGroup
            style={{
              height: "33px",
            }}
            alignItems="center"
            gutterSize="s">
            {isSingleSelection && (
              <EuiFlexItem grow={false}>
                <TagGroupIcon size="18" />
              </EuiFlexItem>
            )}
            {!isSingleSelection && (
              <CustomCheckbox
                onClick={handleCheckboxClick}
                isChecked={areAllSelected}
                isExcluded={areAllNegativeSelected}
                isPartial={totalCheckedCount > 0}
              />
            )}
            <EuiFlexItem>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  fontSize: option?.name?.length > 50 ? "11px" : "14px",
                }}>
                {option.display ?? option.name}
              </span>
            </EuiFlexItem>
            {totalCheckedCount > 0 && (
              <EuiFlexItem grow={false}>
                <EuiNotificationBadge>{totalCheckedCount}</EuiNotificationBadge>
              </EuiFlexItem>
            )}
          </EuiFlexGroup>
        }
        paddingSize="s"
        buttonProps={{
          style: {
            width: "100%",
            display: "block",
          },
        }}
        forceState={isOpen ? "open" : "closed"}
        onToggle={(isOpen) => {
          if (onToggle) {
            onToggle(option.name, isOpen);
          }
        }}
        extraAction={null}
        arrowDisplay="right">
        <div style={{ marginTop: "0" }}>{filteredTagOptions.map(renderTagOption)}</div>
      </EuiAccordion>
    </div>
  );
};

const FDTagFilterDropdown = ({
  FdType,
  OptionsFunc = null,
  Items = [],
  Name = "",
  Field = "",
  OnClick = null,
  UpdateGlobalFilter = true,
  MaxSelectedItems = null,
  OnMaxSelection = null,
  SingleSelection = false,
  AllowExclude = true,
  IsItemVisibleFunc = null,
  ForceSelectedIndex = -1,
  ForceSelectedValue = null,
  ID = null,
  popoverButtonProps = {},
  tagView = null,
}: any) => {
  const classes = useDropdownStyles();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [items, setItems] = useState(Items || []);
  const [activeFilters, setActiveFilters] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [isSelectAll, setSelectAll] = useState(false);
  const [stableOrder, setStableOrder] = useState([]);
  const [openAccordions, setOpenAccordions] = useState<any>({});
  const { currentUser } = useSelector(authSelector, shallowEqual);

  const {
    clientId,
    GlobalTopFilterObject,
    LocalTopFilterObject,
    IsGlobalFilterMode,
    DatePickerParams,
    isTagFilterRefreshed,
  } = useSelector(globalFilterSelector, shallowEqual);

  const ADMIN_CLIENT_ID = currentUser?.clientID;
  const isEnterpriseAdmin = location?.pathname?.includes("/admin/overview");
  const _clientId = isEnterpriseAdmin ? ADMIN_CLIENT_ID : clientId;

  const getSelectedFilters = () => {
    const selectedFilterMode = IsGlobalFilterMode ? GlobalTopFilterObject : LocalTopFilterObject;
    const filterArr = selectedFilterMode[Field];

    if (
      SingleSelection &&
      (filterArr?.length > 1 ||
        (filterArr?.length === 1 && filterArr[0]?.startsWith(NEGATE_FILTER)))
    ) {
      return [];
    } else return selectedFilterMode[Field];
  };

  const currentFilterValue = getSelectedFilters();

  useEffect(() => {
    const onResetEventKey = `FDFilterDropdown_onreset_${ID}`;
    if (ID) {
      Emitter.on(onResetEventKey, () => {
        resetSelected();
      });
    }
    return () => {
      if (ID) {
        //@ts-ignore
        Emitter.off(onResetEventKey);
      }
    };
  }, [ID]);

  const onSingleSelectionClick = (selectedItem: any) => {
    const newFilterStatusList = { [selectedItem.name]: "on" };
    if (ForceSelectedIndex == -1 && activeFilters[selectedItem.name] == "on") {
      delete newFilterStatusList[selectedItem.name];
    }

    setActiveFilters(newFilterStatusList);

    if (OnClick) {
      //@ts-ignore
      OnClick({
        display: selectedItem.display,
        list: newFilterStatusList,
        listItems: items?.filter((i: any) => Object?.keys(newFilterStatusList)?.includes(i.name)),
        name: selectedItem.name,
        selectedItem,
        value: "on",
      });
    }
  };

  const publishGlobalFilter = useCallback(
    async (values: any) => {
      if (UpdateGlobalFilter) {
        const newFieldGlobalFilterValues = AllowExclude
          ? Object.entries(values).map(([listItem, value]) => {
              if (value === "off") {
                return `${NEGATE_FILTER}${listItem}`;
              }
              return listItem;
            })
          : Object.entries(values)
              .filter(([_, value]) => value !== "off")
              .map(([listItem, _]) => listItem);

        if (!AllowExclude) setActiveFilters({});

        dispatch({
          payload: {
            [Field]: newFieldGlobalFilterValues.length > 0 ? newFieldGlobalFilterValues : null,
          },
          type: globalFilterTypes.UPDATE_TOP_FILTER,
        });

        if (Field === "tag") {
          const includeTags = newFieldGlobalFilterValues
            .filter((tag) => !tag.startsWith(NEGATE_FILTER))
            .map((tag) => {
              const [name, value] = tag.split(": ");
              return { name, value };
            });

          const excludeTags = newFieldGlobalFilterValues
            .filter((tag) => tag.startsWith(NEGATE_FILTER))
            .map((tag) => {
              const [name, value] = tag.replace(NEGATE_FILTER, "").split(": ");
              return { name, value };
            });

          const tagFilter = { includeTags, excludeTags };

          try {
            const dependentOptions = await LookupService.getResourceFilters(clientId, {
              tagFilter,
              filterTypes: [FilterType.Instance, FilterType.Host, FilterType.Database],
            });
            Emitter.emit("TAG_FILTERS_UPDATED", dependentOptions);
          } catch (error) {
            console.error("Error fetching dependent filters:", error);
            Emitter.emit("TAG_FILTERS_UPDATED", {
              serverFilters: [],
              hostFilters: [],
              instanceFilters: [],
              databaseFilters: [],
            });
          }
        }
      }
    },
    [Field, UpdateGlobalFilter, clientId, dispatch, AllowExclude]
  );

  const isLimitReached = (currentChecked: any) => {
    if (MaxSelectedItems && currentChecked === undefined) {
      const excludedPlusIncludedItems = Object.values(activeFilters).filter((v) => v)?.length;

      if (excludedPlusIncludedItems >= MaxSelectedItems && OnMaxSelection) {
        //@ts-ignore
        OnMaxSelection();
      }
      return excludedPlusIncludedItems >= MaxSelectedItems;
    }
    return false;
  };

  useEffect(() => {
    if (isSelectAll && items?.length !== GlobalTopFilterObject[Field]?.length) {
      setSelectAll(false);
    }
  }, [GlobalTopFilterObject]);

  useEffect(() => {
    if (!FdType) return;
    getDropdownData({
      DatePickerParams,
      GlobalTopFilterObject,
      clientId: _clientId,
      fdType: FdType,
      setItems,
      setLoading,
      tagView,
    });
  }, [FdType, _clientId, isTagFilterRefreshed]);

  useEffect(() => {
    if (!OptionsFunc) return;

    setLoading(true);
    //@ts-ignore
    OptionsFunc.then((result) => {
      const _items = result?.data?.map((t: any) => ({
        display: t.display,
        name: t.name || t.type,
        value: getFirstNonNullUndefined([t.value, t.id, t.type, t.name]),
      }));

      setItems(_items ?? []);
      setLoading(false);
    });
  }, [OptionsFunc]);

  useEffect(() => {
    if (Items) {
      const cleanItems = Items.filter(
        (option: any) => option.value !== undefined && option.value !== null
      );

      const mappedItems = cleanItems.map((f: any) => ({
        ...f,
        name: f.value,
      }));

      setItems(mappedItems);
    }
  }, [Items]);

  useEffect(() => {
    const map: any = {};
    if (currentFilterValue) {
      for (const filterVal of currentFilterValue) {
        if (filterVal?.startsWith(NEGATE_FILTER)) {
          map[filterVal.replace(NEGATE_FILTER, "")] = "off";
        } else {
          map[filterVal] = "on";
        }
      }

      setActiveFilters(map);
    } else {
      resetSelected();
    }
  }, [currentFilterValue, UpdateGlobalFilter, JSON.stringify(items)]);

  const togglePopover = () => {
    const newIsOpen = !isPopoverOpen;
    if (newIsOpen) {
      recalculateOrder();
    }
    setIsPopoverOpen(newIsOpen);
  };

  const closePopover = () => {
    setIsPopoverOpen(false);
    setOpenAccordions({});
  };

  const visibleItems = useMemo(() => {
    const _items = mergeByName(items);
    const result = IsItemVisibleFunc
      ? //@ts-ignore
        _items.filter((item: any) => IsItemVisibleFunc(item.display ?? item.name))
      : _items;

    const selected = result.filter((i: any) => GlobalTopFilterObject[Field]?.includes(i.name));
    const excluded = result.filter((i: any) =>
      GlobalTopFilterObject[Field]?.includes(`${NEGATE_FILTER}${i.name}`)
    );

    const nSelected = result
      .filter(
        (i: any) =>
          !GlobalTopFilterObject[Field]?.includes(i.name) &&
          !GlobalTopFilterObject[Field]?.includes(`${NEGATE_FILTER}${i.name}`)
      )
      .sort((a: any, b: any) => {
        return a.display ? a.display.localeCompare(b.display) : a.name?.localeCompare(b.name);
      });

    return [...selected, ...excluded, ...nSelected];
  }, [IsItemVisibleFunc, items]);

  const resetSelected = useCallback(() => {
    if (ForceSelectedIndex > -1 && items.length > 0) {
      const selectedItem: any = items[ForceSelectedIndex];
      if (selectedItem && selectedItem.name) {
        const activeFiltersCopy = { ...activeFilters };
        activeFiltersCopy[selectedItem.name] = "on";

        setActiveFilters(activeFiltersCopy);
      }
    } else if (!isNullOrUndefined(ForceSelectedValue) && items.length > 0) {
      const arrSelected = Array.isArray(ForceSelectedValue)
        ? ForceSelectedValue
        : [ForceSelectedValue];

      const filterListStatusCopy = SingleSelection ? {} : { ...activeFilters };
      arrSelected.forEach((selected) => {
        const selectedItemIndex = items.findIndex((category: any) => category.value == selected);
        if (selectedItemIndex > -1) {
          const selectedItem: any = items[selectedItemIndex];

          if (selectedItem && selectedItem.name) {
            filterListStatusCopy[selectedItem.name] = "on";
          }
        }
      });

      setActiveFilters(filterListStatusCopy);
    } else {
      if (FdType || Object.keys(GlobalTopFilterObject).length === 0) setActiveFilters({});
    }
  }, [
    ForceSelectedIndex,
    ForceSelectedValue,
    Name,
    JSON.stringify(activeFilters),
    JSON.stringify(items),
    GlobalTopFilterObject,
  ]);

  useEffect(() => {
    resetSelected();
  }, [ForceSelectedIndex]);

  useEffect(() => {
    resetSelected();
  }, [ForceSelectedValue]);

  useEffect(
    () => () => {
      setItems([]);
      setLoading(false);
    },
    []
  );

  const activeFiltersCount = Object.keys(activeFilters).length;

  const filterArrowDownButton = (
    <EuiFilterButton
      className={
        activeFiltersCount == 0
          ? "euiFilterButton__notification_hidden filter_button"
          : "filter_button"
      }
      data-test-subj={`filter-dd-${Name}`}
      hasActiveFilters={activeFiltersCount > 0}
      iconType="arrowDown"
      iconSize="s"
      isSelected={isPopoverOpen}
      numActiveFilters={activeFiltersCount}
      numFilters={visibleItems.length}
      onClick={togglePopover}
      {...popoverButtonProps}>
      <span style={{ position: "absolute", top: "5px" }}>
        <EuiIcon type="tag" />
      </span>
      <span style={{ paddingLeft: "20px" }}>{Name}</span>
    </EuiFilterButton>
  );

  const filterItemClick = (item: any) => {
    setSelectAll(false);
    const currentChecked = activeFilters[item.name];
    let newChecked;

    if (AllowExclude) {
      if (currentChecked !== undefined) {
        if (currentChecked === "on") {
          newChecked = "off";
        } else if (currentChecked === "off" || currentChecked === "on") {
          newChecked = undefined;
        }
      } else {
        newChecked = "on";
      }
    }

    if (!AllowExclude) {
      newChecked = currentChecked === "on" ? undefined : "on";
    }

    if (SingleSelection) {
      onSingleSelectionClick(item);
      return;
    } else {
      if (isLimitReached(currentChecked)) {
        return;
      }
    }

    const filterListStatusCopy = { ...activeFilters };

    if (newChecked === undefined) {
      delete filterListStatusCopy[item.name];
    } else {
      filterListStatusCopy[item.name] = newChecked;
    }

    setActiveFilters(filterListStatusCopy);

    if (OnClick) {
      //@ts-ignore
      OnClick({
        list: filterListStatusCopy,
        listItems: items?.filter((i: any) => Object?.keys(filterListStatusCopy)?.includes(i.name)),
        name: item.name,
        selectedItem: item,
        value: currentChecked === "on" ? "off" : "on",
      });
    }
  };

  const applyFilters = () => {
    setFilterText("");
    publishGlobalFilter(activeFilters);
    closePopover();
    recalculateOrder();
  };

  const cancelFiltersSelection = () => {
    const cancelFiltersState: any = {};

    Object.entries(activeFilters).forEach(([key, value]) => {
      if (GlobalTopFilterObject[Field]?.includes(key)) {
        cancelFiltersState[key] = "on";
      }
      if (GlobalTopFilterObject[Field]?.includes(`${NEGATE_FILTER}${key}`)) {
        cancelFiltersState[key] = "off";
      }
    });
    setFilterText("");
    setActiveFilters(cancelFiltersState);
    if (isSelectAll) setSelectAll(false);
    closePopover();
  };

  const serachId = useGeneratedHtmlId();

  const dropdownOptions: any[] = visibleItems
    .filter((item: any) => {
      const accordionHeaderMatches =
        filterText.length === 0 ||
        (item.name + "").toLowerCase().includes(filterText.toLowerCase()) ||
        (item?.display ?? "").toLowerCase().includes(filterText.toLowerCase());

      if (accordionHeaderMatches) return true;

      if (item.values && item.values.length > 0) {
        return item.values.some((tagOption: any) => {
          const tagValue = tagOption.value || "";
          const tagDisplay = tagOption.display || tagValue;

          return (
            tagValue.toLowerCase().includes(filterText.toLowerCase()) ||
            tagDisplay.toLowerCase().includes(filterText.toLowerCase())
          );
        });
      }

      return false;
    })
    .sort((a: any, b: any) => a.name - b.name)
    .map((i) => ({
      ...i,
      label: i.name,
    }));

  const getCheckedCountForOption = (option: any) => {
    return (
      option.values?.reduce((count: any, item: any) => {
        const fullTagName = `${option.name}: ${item?.value}`;
        return activeFilters[fullTagName] === "on" || activeFilters[fullTagName] === "off"
          ? count + 1
          : count;
      }, 0) || 0
    );
  };

  const recalculateOrder = useCallback(() => {
    const filteredItems = visibleItems.filter((item) => {
      return (
        filterText.length === 0 ||
        (item.name + "").toLowerCase().includes(filterText.toLowerCase()) ||
        (item?.display ?? "").toLowerCase().includes(filterText.toLowerCase())
      );
    });

    const newOrder = [...filteredItems].sort((a, b) => {
      const aCheckedCount = getCheckedCountForOption(a);
      const bCheckedCount = getCheckedCountForOption(b);

      if (aCheckedCount > 0 && bCheckedCount === 0) return -1;
      if (aCheckedCount === 0 && bCheckedCount > 0) return 1;

      if (aCheckedCount > 0 && bCheckedCount > 0) {
        return bCheckedCount - aCheckedCount;
      }

      const aText = a.display || a.name;
      const bText = b.display || b.name;
      return aText.localeCompare(bText);
    });

    //@ts-ignore
    setStableOrder(newOrder.map((item) => item.tagId));
  }, [visibleItems, filterText, activeFilters]);

  useEffect(() => {
    if (visibleItems.length > 0) {
      recalculateOrder();
    }
  }, [visibleItems]);

  const handleAccordionToggle = (optionName: any, isOpen: any) => {
    if (isOpen) {
      setOpenAccordions({ [optionName]: true });
    } else {
      setOpenAccordions((prev: any) => {
        const newOpenAccordions: any = { ...prev };
        delete newOpenAccordions[optionName];
        return newOpenAccordions;
      });
    }
  };

  const getSortedDropdownOptions = () => {
    if (stableOrder.length === 0) {
      return dropdownOptions;
    }

    const itemMap: any = {};
    dropdownOptions.forEach((item) => {
      itemMap[item.tagId] = item;
    });

    return stableOrder.filter((tagId) => itemMap[tagId]).map((name) => itemMap[name]);
  };

  useEffect(() => {
    if (filterText.length > 0) {
      const newOpenAccordions: any = {};

      visibleItems.forEach((item) => {
        const headerMatches =
          (item.name + "").toLowerCase().includes(filterText.toLowerCase()) ||
          (item?.display ?? "").toLowerCase().includes(filterText.toLowerCase());

        if (item.values && item.values.length > 0) {
          const hasMatchingChildren = item.values.some((tagOption: any) => {
            const tagValue = tagOption.value || "";
            const tagDisplay = tagOption.display || tagValue;

            return (
              tagValue.toLowerCase().includes(filterText.toLowerCase()) ||
              tagDisplay.toLowerCase().includes(filterText.toLowerCase())
            );
          });

          if (headerMatches || hasMatchingChildren) {
            newOpenAccordions[item.name] = true;
          }
        } else if (headerMatches) {
          newOpenAccordions[item.name] = true;
        }
      });

      setOpenAccordions(newOpenAccordions);
    } else if (filterText.length === 0) {
      setOpenAccordions({});
    }
  }, [filterText, visibleItems]);
  return (
    <>
      {
        <Popover
          noCloseOnScroll
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.stopPropagation();
              cancelFiltersSelection();
            }
          }}
          button={filterArrowDownButton}
          onClose={cancelFiltersSelection}
          isOpen={isPopoverOpen}
          ownFocus
          children={
            <div
              style={{
                backgroundColor: "var(--card-background)",
                borderRadius: "var(--box-radius)",
              }}>
              <EuiPopoverTitle className={classes.filterPopoverTitle}>
                <EuiFieldSearch
                  className={`${classes.search_input} filter-search-input`}
                  id={serachId}
                  isClearable
                  onChange={(e) => {
                    setFilterText(e.target.value);
                  }}
                  style={{
                    borderBottom: SingleSelection ? "none" : ".6px solid var(--border-color)",
                    backgroundColor: "var(--card-background)",
                  }}
                  value={filterText}
                />
              </EuiPopoverTitle>

              <div
                className="euiFilterSelect__items"
                style={{
                  overflowY: "auto",
                  maxHeight: "500px",
                  scrollbarWidth: "thin",
                  minWidth: "400px",
                }}>
                {loading ? (
                  <div style={{ display: "flex", justifyContent: "center", padding: "20px" }}>
                    <EuiFlexGroup
                      justifyContent="center"
                      alignItems="center"
                      direction="column"
                      gutterSize="s">
                      <EuiFlexItem grow={false}>
                        <EuiLoadingSpinner size="s" />
                      </EuiFlexItem>
                      <EuiFlexItem grow={false}>
                        <EuiText>
                          <p style={{ fontSize: "var(--fs12)", color: "#516381" }}>
                            Loading filters
                          </p>
                        </EuiText>
                      </EuiFlexItem>
                    </EuiFlexGroup>
                  </div>
                ) : getSortedDropdownOptions().length > 0 ? (
                  getSortedDropdownOptions().map((option) => (
                    <TagAccordionItem
                      key={option.name || option.label}
                      option={option}
                      activeFilters={activeFilters}
                      filterItemClick={filterItemClick}
                      classes={classes}
                      setActiveFilters={setActiveFilters}
                      OnClick={OnClick}
                      items={items}
                      onToggle={handleAccordionToggle}
                      isOpen={!!openAccordions[option.name]}
                      filterText={filterText}
                    />
                  ))
                ) : (
                  <div style={{ padding: "16px", textAlign: "center" }}>No filters found</div>
                )}
              </div>
              <FDFilterDropdownFooter onApply={applyFilters} onCancel={cancelFiltersSelection} />
            </div>
          }
        />
      }
    </>
  );
};

export default FDTagFilterDropdown;
